import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, Edit, Trash2, Calendar, DollarSign, Gift, Smartphone, Globe } from 'lucide-react';
import Button from '../components/UI/Button';
import Table from '../components/UI/Table';
import Modal from '../components/UI/Modal';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { Booking } from '../types';
import { extractTourText } from '../utils/tourUtils';

const Bookings: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterUser, setFilterUser] = useState('');
  const [filterTour, setFilterTour] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'playstore' | 'appstore' | 'bokun' | 'redeem'>('all');
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [sortColumn, setSortColumn] = useState<keyof Booking>('bookingDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Confirmation dialog state
  const [confirmationDialog, setConfirmationDialog] = useState<{
    show: boolean;
    message: string;
    onConfirm: () => void;
  }>({
    show: false,
    message: '',
    onConfirm: () => {},
  });

  // Fetch bookings
  const fetchBookings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getBookings(1, 1000, {
        filterUser: filterUser || undefined,
        filterTour: filterTour || undefined,
        filterType: filterType === 'all' ? undefined : filterType,
      });
      setBookings(response.bookings || []);
    } catch (error: any) {
      const message = error?.message || 'Failed to load bookings';
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUser, filterTour, filterType]);

  // Filter bookings (client-side filtering for additional refinement if needed)
  // Note: Main filtering is done server-side, but we keep this for any additional client-side filtering
  const filteredBookings = bookings;

  // Sort bookings
  const sortedBookings = [...filteredBookings].sort((a, b) => {
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    
    if (aValue === undefined || bValue === undefined) return 0;
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (aValue instanceof Date && bValue instanceof Date) {
      return sortDirection === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });

  const handleSort = (column: keyof Booking) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleView = (booking: Booking) => {
    setSelectedBooking(booking);
    setIsViewModalOpen(true);
  };

  const handleDelete = async (bookingId: number) => {
    const booking = bookings.find(b => b.id === bookingId);
    const bookingName = booking?.tour?.name 
      ? extractTourText(booking.tour.name) 
      : booking?.bundle?.name || 'this booking';
    
    setConfirmationDialog({
      show: true,
      message: `Are you sure you want to delete ${bookingName}? This action cannot be undone.`,
      onConfirm: async () => {
        setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
        try {
          await apiService.deleteBooking(bookingId);
          toast.success('Booking deleted successfully!');
          await fetchBookings();
        } catch (error) {
          toast.error('Failed to delete booking');
        }
      },
    });
  };

  const getPurchaseTypeIcon = (type: string) => {
    switch (type) {
      case 'playstore':
        return <Smartphone className="h-4 w-4 text-green-600" />;
      case 'appstore':
        return <Smartphone className="h-4 w-4 text-blue-600" />;
      case 'bokun':
        return <Gift className="h-4 w-4 text-orange-600" />;
      case 'redeem':
        return <Gift className="h-4 w-4 text-orange-600" />;
      default:
        return <DollarSign className="h-4 w-4 text-gray-600" />;
    }
  };

  const getPurchaseTypeLabel = (type: string) => {
    switch (type) {
      case 'playstore':
        return 'Play Store';
      case 'appstore':
        return 'App Store';
      case 'bokun':
        return 'Bokun (Redeem)';
      case 'redeem':
        return 'Bokun (Redeem)';
      default:
        return type;
    }
  };

  const columns = [
    {
      key: 'user' as keyof Booking,
      header: 'User',
      sortable: false,
      render: (value: any, booking: Booking) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
            <span className="text-sm font-semibold text-white">
              {booking.user?.firstName?.charAt(0)?.toUpperCase() || booking.user?.email?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 max-w-[200px] truncate">
              {booking.user?.firstName && booking.user?.lastName
                ? `${booking.user.firstName} ${booking.user.lastName}`
                : booking.user?.firstName || 'N/A'
              }
            </p>
            <p className="text-sm text-gray-500 max-w-[200px] truncate">{booking.user?.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'tour' as keyof Booking,
      header: 'Tour/Bundle',
      sortable: false,
      render: (value: any, booking: Booking) => (
        <div>
          {booking.tour ? (
            <>
              <p className="font-medium text-gray-900 max-w-[200px] truncate">{extractTourText(booking.tour.name)}</p>
              <p className="text-sm text-gray-500 max-w-[200px] truncate">{booking.tour.place || 'N/A'}</p>
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                Tour
              </span>
            </>
          ) : booking.bundle ? (
            <>
              <p className="font-medium text-gray-900 max-w-[200px] truncate">{booking.bundle.name}</p>
              <p className="text-sm text-gray-500 max-w-[200px] truncate">{booking.bundle.place || 'N/A'}</p>
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                Bundle
              </span>
            </>
          ) : (
            <span className="text-sm text-gray-400">N/A</span>
          )}
        </div>
      ),
    },
    {
      key: 'purchaseType' as keyof Booking,
      header: 'Purchase Type',
      sortable: true,
      render: (value: any, booking: Booking) => (
        <div className="flex items-center space-x-2">
          {getPurchaseTypeIcon(booking.purchaseType)}
          <span className="text-sm text-gray-900">{getPurchaseTypeLabel(booking.purchaseType)}</span>
        </div>
      ),
    },
    {
      key: 'bookingDate' as keyof Booking,
      header: 'Booking Date',
      sortable: true,
      render: (value: any, booking: Booking) => (
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-900">
            {booking.bookingDate ? new Date(booking.bookingDate).toLocaleDateString() : 'N/A'}
          </span>
        </div>
      ),
    },
    {
      key: 'redeemInfo' as keyof Booking,
      header: 'Payment/Redeem Info',
      sortable: false,
      render: (value: any, booking: Booking) => (
        <div>
          {booking.purchaseType === 'redeem' || booking.purchaseType === 'bokun' ? (
            <div className="text-sm">
              <p className="text-gray-900">
                {booking.redeemCount || 0} / {booking.totalRedeems || 0} used
              </p>
              <p className="text-gray-500">
                {((booking.totalRedeems || 0) - (booking.redeemCount || 0))} remaining
              </p>
            </div>
          ) : (
            <div className="text-sm">
              <p className="text-gray-900">
                {booking.amount ? `${booking.currency || 'USD'} ${booking.amount.toFixed(2)}` : 'N/A'}
              </p>
              <p className="text-gray-500 max-w-[160px] truncate" title={booking.transactionId || 'N/A'}>{booking.transactionId || 'N/A'}</p>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'actions' as keyof Booking,
      header: 'Actions',
      sortable: false,
      render: (value: any, booking: Booking) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleView(booking)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title="View booking details"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDelete(booking.id)}
            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
            title="Delete booking"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
        <Button onClick={() => fetchBookings()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bookings Management</h1>
          <p className="text-gray-600">Manage all tour bookings and purchases</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by user..."
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by tour..."
              value={filterTour}
              onChange={(e) => setFilterTour(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary appearance-none bg-white"
            >
              <option value="all">All Types</option>
              <option value="playstore">Play Store</option>
              <option value="appstore">App Store</option>
              <option value="redeem">Bokun (Redeem)</option>
            </select>
          </div>
          <div className="flex items-center justify-end">
            <Button onClick={() => fetchBookings()} variant="outline">
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <Table
        data={sortedBookings}
        columns={columns}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        loading={isLoading}
        emptyMessage="No bookings found"
      />

      {/* View Booking Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedBooking(null);
        }}
        title="Booking Details"
        size="lg"
      >
        {selectedBooking && (
          <div className="space-y-6">
            {/* User and Tour Info */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">User Information</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
                      <span className="text-lg font-semibold text-white">
                        {selectedBooking.user?.firstName?.charAt(0)?.toUpperCase() || selectedBooking.user?.email?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {selectedBooking.user?.firstName && selectedBooking.user?.lastName 
                          ? `${selectedBooking.user.firstName} ${selectedBooking.user.lastName}`
                          : selectedBooking.user?.firstName || 'N/A'
                        }
                      </p>
                      <p className="text-gray-600">{selectedBooking.user?.email}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">Phone: {selectedBooking.user?.phone || 'N/A'}</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Tour/Bundle Information</h3>
                <div className="space-y-2">
                  {selectedBooking.tour ? (
                    <>
                      <p className="font-medium text-gray-900">{extractTourText(selectedBooking.tour.name)}</p>
                      <p className="text-sm text-gray-600">Place: {selectedBooking.tour.place || 'N/A'}</p>
                      <p className="text-sm text-gray-600">Duration: {selectedBooking.tour.duration || 'N/A'}</p>
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        Tour
                      </span>
                    </>
                  ) : selectedBooking.bundle ? (
                    <>
                      <p className="font-medium text-gray-900">{selectedBooking.bundle.name}</p>
                      <p className="text-sm text-gray-600">Place: {selectedBooking.bundle.place || 'N/A'}</p>
                      <p className="text-sm text-gray-600">Description: {selectedBooking.bundle.description || 'N/A'}</p>
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                        Bundle
                      </span>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400">No tour/bundle information available</p>
                  )}
                </div>
              </div>
            </div>

            {/* Booking Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Booking Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Purchase Type</label>
                  <div className="flex items-center space-x-2 mt-1">
                    {getPurchaseTypeIcon(selectedBooking.purchaseType)}
                    <span className="text-sm text-gray-900">{getPurchaseTypeLabel(selectedBooking.purchaseType)}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Booking Date</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedBooking.bookingDate ? new Date(selectedBooking.bookingDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Transaction ID</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedBooking.transactionId || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Payment/Redeem Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {selectedBooking.purchaseType === 'redeem' || selectedBooking.purchaseType === 'bokun' ? 'Redeem Information' : 'Payment Information'}
              </h3>
              {selectedBooking.purchaseType === 'redeem' || selectedBooking.purchaseType === 'bokun' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Total Redeems</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedBooking.totalRedeems || 0}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Used Redeems</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedBooking.redeemCount || 0}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Remaining Redeems</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {((selectedBooking.totalRedeems || 0) - (selectedBooking.redeemCount || 0))}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Amount</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {selectedBooking.amount ? `${selectedBooking.currency || 'USD'} ${selectedBooking.amount.toFixed(2)}` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Transaction ID</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedBooking.transactionId || 'N/A'}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsViewModalOpen(false);
                  setSelectedBooking(null);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Confirmation Dialog */}
      {confirmationDialog.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirm Delete
            </h3>
            <p className="text-sm text-gray-700 mb-6">
              {confirmationDialog.message}
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={confirmationDialog.onConfirm}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings; 